import React, { useState, useEffect, useCallback, useMemo } from "react";
import Header from "./app/modules/layout/Header";
import Sidebar from "./app/modules/layout/Sidebar";
import WelcomePage from "./app/modules/customer/pages/WelcomePage";
import MainContent from "./app/modules/customer/pages/MainContent";
import ShareReferencesContent from "./app/modules/customer/pages/ShareReferencesContent";
import UpdateIncomeContent from "./app/modules/customer/pages/UpdateIncomeContent";
import FewQuestionsContent from "./app/modules/customer/pages/FewQuestionsContent";
import BottomFooterBar from "./app/modules/layout/BottomFooterBar";
import QuickJumpNav from "./app/modules/layout/QuickJumpNav";
import MobileBottomNav from "./app/modules/layout/MobileBottomNav";
import InitialLandingPage from "./app/modules/customer/pages/InitialLandingPage";
import ChooseLoanPage from "./app/modules/customer/pages/ChooseLoanPage";
import IncomeOnlyPage from "./app/modules/customer/pages/IncomeOnlyPage";
import CircleSpinner from "./app/common/loader/circleSpinner";
import "react-toastify/dist/ReactToastify.css";

import { ApplicationStatusDto, Step, StepStatus } from "./app/common/types";
import {
  CO_APPLICANT_STEP_IDS,
  INITIAL_STEPS,
  USER_NAME,
  customerInfoIdValue,
  getStepsForApplicantType,
  userNameValue,
} from "./app/common/helperConstants";
import { applicationsApi } from "./app/services/api";
import { getStorage, setStorage } from "./app/common/storageHelper";
import { useIdleLogout } from "./app/common/hooks/useIdleLogout";
import {
  useGoogleTagManagerInit,
  pushUserUpdateEvent,
} from "./app/common/hooks/useGoogleTagManager";
import {
  deriveCompletionStatus,
  getCustomerEmail,
  getMobileContentPaddingClass,
} from "./app/common/helperMethods";

type AppStage = "initialLanding" | "chooseLoan" | "mainApp" | "incomeOnly";

const INCOME_STEP_ID = 4;

const App: React.FC = () => {
  // ---------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------
  const [steps, setSteps] = useState<Step[]>(() =>
    INITIAL_STEPS.map((s) => ({ ...s })),
  );
  const [userName, setUserName] = useState("");
  const [lastName, setLastName] = useState("");
  const [userEmail, setUserEmail] = useState("");
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [currentViewedStepId, setCurrentViewedStepId] = useState(0);
  const [appStage, setAppStage] = useState<AppStage>("initialLanding");
  const [stepCompletionStatus, setStepCompletionStatus] = useState<
    Record<number, boolean>
  >({});
  const [isLoader, setIsLoader] = useState(false);
  const [isCoApplicant, setIsCoApplicant] = useState(false);

  const visibleSteps = useMemo(
    () =>
      isCoApplicant
        ? steps.filter((s) => CO_APPLICANT_STEP_IDS.includes(s.id))
        : steps,
    [steps, isCoApplicant],
  );

  // ---------------------------------------------------------------------
  // Cross-cutting concerns
  // ---------------------------------------------------------------------
  useIdleLogout();
  useGoogleTagManagerInit();

  // Reset the viewed step whenever the applicant type changes, so we never
  // land on a step that isn't in the (possibly narrower) co-applicant set.
  useEffect(() => {
    setCurrentViewedStepId(0);
  }, [isCoApplicant]);

  // Fetch application status once the user reaches "chooseLoan". This is
  // also where the new isFromIncome redirect is decided.
  useEffect(() => {
    if (appStage !== "chooseLoan") return;

    const infoId = getStorage(customerInfoIdValue);
    if (!infoId) return;

    let isCancelled = false;

    const fetchUserData = async () => {
      try {
        setIsLoader(true);
        const response: ApplicationStatusDto =
          await applicationsApi.getStatus(infoId);

        if (isCancelled || !response?.customerName) return;

        const coApplicantFlag = response.isCoApplicant === true;

        setIsCoApplicant(coApplicantFlag);
        setSteps(getStepsForApplicantType(coApplicantFlag, response.appStatus));
        setUserName(response.customerName);
        setLastName(response.lastName);
        setUserEmail(getCustomerEmail(response, coApplicantFlag));
        setStorage(userNameValue, response.customerName);

        // NEW: bypass loan selection, tabs, and side nav entirely and land
        // directly on the income step — applies to both applicant and
        // co-applicant whenever the backend flags the application this way.
        if (response.isFromIncomeOnly === true) {
          setCurrentViewedStepId(INCOME_STEP_ID);
          setAppStage("incomeOnly");
          return;
        }

        setStepCompletionStatus(
          deriveCompletionStatus(response, coApplicantFlag),
        );
      } catch {
        setUserName(getStorage(userNameValue) || USER_NAME);
      } finally {
        if (!isCancelled) setIsLoader(false);
      }
    };

    fetchUserData();

    return () => {
      isCancelled = true;
    };
  }, [appStage]);

  // Lightweight "page view" style analytics event on navigation.
  useEffect(() => {
    pushUserUpdateEvent();
    window.scrollTo(0, 0);
  }, [appStage, currentViewedStepId]);

  // ---------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------
  const toggleMobileMenu = useCallback(
    () => setIsMobileMenuOpen((open) => !open),
    [],
  );

  const closeMobileMenu = useCallback(() => setIsMobileMenuOpen(false), []);

  const markStepAsCompleted = useCallback((stepId: number) => {
    setSteps((prevSteps) =>
      prevSteps.map((step) =>
        step.id === stepId && step.status !== StepStatus.COMPLETED
          ? { ...step, status: StepStatus.COMPLETED }
          : step,
      ),
    );
  }, []);

  const handleStepSelect = useCallback(
    (stepId: number) => {
      setCurrentViewedStepId(stepId);
      closeMobileMenu();
    },
    [closeMobileMenu],
  );

  const handleGoToAllItems = useCallback(() => {
    setCurrentViewedStepId(0);
    closeMobileMenu();
  }, [closeMobileMenu]);

  const handleGoToInitialLandingPage = useCallback(() => {
    setAppStage("initialLanding");
  }, []);

  const isStepCompleted = useCallback(
    (stepId: number) =>
      stepCompletionStatus[stepId] === true ||
      visibleSteps.find((s) => s.id === stepId)?.status ===
        StepStatus.COMPLETED,
    [stepCompletionStatus, visibleSteps],
  );

  const handleNextStep = useCallback(() => {
    const currentIndex = visibleSteps.findIndex(
      (s) => s.id === currentViewedStepId,
    );

    if (currentIndex !== -1 && currentIndex < visibleSteps.length - 1) {
      setCurrentViewedStepId(visibleSteps[currentIndex + 1].id);
      closeMobileMenu();
      return;
    }

    if (currentIndex === visibleSteps.length - 1) {
      const nextPending = visibleSteps.find(
        (s) => s.id !== currentViewedStepId && !isStepCompleted(s.id),
      );
      setCurrentViewedStepId(nextPending ? nextPending.id : 0);
      closeMobileMenu();
      return;
    }

    setCurrentViewedStepId(0);
    closeMobileMenu();
  }, [visibleSteps, currentViewedStepId, isStepCompleted, closeMobileMenu]);

  const handleBackStep = useCallback(() => {
    if (currentViewedStepId === 0) return;

    const currentIndex = visibleSteps.findIndex(
      (s) => s.id === currentViewedStepId,
    );
    setCurrentViewedStepId(
      currentIndex > 0 ? visibleSteps[currentIndex - 1].id : 0,
    );
    closeMobileMenu();
  }, [currentViewedStepId, visibleSteps, closeMobileMenu]);

  const handleGoToChooseLoan = useCallback(() => {
    setAppStage("chooseLoan");
  }, []);

  const handleLoanSelected = useCallback(() => {
    setAppStage("mainApp");
    setCurrentViewedStepId(0);
  }, []);

  const handleIncomeOnlyComplete = useCallback(() => {
    markStepAsCompleted(INCOME_STEP_ID);
  }, [markStepAsCompleted]);

  // ---------------------------------------------------------------------
  // Content resolution for the shared chooseLoan/mainApp layout
  // ---------------------------------------------------------------------
  const renderPrimaryContent = () => {
    if (appStage === "chooseLoan") {
      if (isLoader) {
        return (
          <div className="loaderGrey">
            <CircleSpinner />
          </div>
        );
      }
      return <ChooseLoanPage onLoanSelected={handleLoanSelected} />;
    }

    if (currentViewedStepId === 0) {
      return (
        <WelcomePage
          userName={userName}
          steps={steps}
          onStepSelect={handleStepSelect}
          mainContentClassName="flex-grow md:h-full"
          stepCompletionStatus={stepCompletionStatus}
          isLoader={isLoader}
        />
      );
    }

    const commonProps = {
      onNextStep: handleNextStep,
      mainContentClassName: "flex-grow md:h-full",
      markStepAsCompleted,
      currentStepId: currentViewedStepId,
      visibleSteps,
    };

    switch (currentViewedStepId) {
      case 1:
      case 2:
      case 6:
        return <MainContent {...commonProps} />;
      case 3:
        return <ShareReferencesContent {...commonProps} />;
      case 4:
        return <UpdateIncomeContent {...commonProps} />;
      case 5:
        return <FewQuestionsContent {...commonProps} />;
      default:
        return (
          <main className="flex-grow bg-white p-4 sm:p-6 rounded-lg shadow-lg text-base">
            <p>Invalid step. Please select a step from the menu.</p>
          </main>
        );
    }
  };

  // ---------------------------------------------------------------------
  // Stage: initial OTP / landing — isolated full-page layout
  // ---------------------------------------------------------------------
  if (appStage === "initialLanding") {
    return <InitialLandingPage onGoToChooseLoan={handleGoToChooseLoan} />;
  }

  // ---------------------------------------------------------------------
  // Stage: direct-to-income — header + income form + footer only.
  // No ChooseLoanPage, Sidebar, QuickJumpNav, or MobileBottomNav.
  // ---------------------------------------------------------------------
  if (appStage === "incomeOnly") {
    const incomeStep = steps.find((s) => s.id === INCOME_STEP_ID);
    return (
      <IncomeOnlyPage
        userName={userName}
        lastName={lastName}
        userEmail={userEmail}
        incomeStep={incomeStep}
        markStepAsCompleted={markStepAsCompleted}
        onComplete={handleIncomeOnlyComplete}
        onLogoClick={handleGoToInitialLandingPage}
      />
    );
  }

  // ---------------------------------------------------------------------
  // Stage: chooseLoan / mainApp — shared layout (Header + Footer, with
  // Sidebar/QuickJumpNav/MobileBottomNav only while inside a mainApp step)
  // ---------------------------------------------------------------------
  const isBackDisabled = currentViewedStepId === 0;
  const isMobileBottomNavActive = currentViewedStepId !== 0;
  const isQuickJumpNavVisibleOnMobile =
    !isMobileMenuOpen && currentViewedStepId !== 0;
  const showSidebarLayout = appStage === "mainApp" && currentViewedStepId !== 0;
  const mobilePaddingClass = getMobileContentPaddingClass(
    isMobileBottomNavActive,
    isQuickJumpNavVisibleOnMobile,
  );

  return (
    <div className="min-h-screen flex flex-col">
      <Header
        userName={userName}
        lastName={lastName}
        onMenuToggle={toggleMobileMenu}
        isMobileMenuOpen={isMobileMenuOpen}
        currentViewedStepId={currentViewedStepId}
        onLogoClick={handleGoToInitialLandingPage}
        userEmail={userEmail}
      />

      <div
        className={`flex-grow w-full max-w-screen-2xl mx-auto px-2 sm:px-4 lg:px-6 xl:px-8 py-4 ${mobilePaddingClass} md:pb-4`}
      >
        {showSidebarLayout ? (
          <div className="flex flex-col md:flex-row gap-4 lg:gap-6 relative md:h-full">
            <Sidebar
              steps={steps}
              currentViewedStepId={currentViewedStepId}
              onStepSelect={handleStepSelect}
              onGoToAllItems={handleGoToAllItems}
              onCloseMenuClick={closeMobileMenu}
              isMobileMenuOpen={isMobileMenuOpen}
              ariaId="mobile-sidebar"
              className={
                isMobileMenuOpen ? "translate-x-0" : "-translate-x-full"
              }
              stepCompletionStatus={stepCompletionStatus}
            />

            {isMobileMenuOpen && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
                onClick={closeMobileMenu}
                aria-hidden="true"
              />
            )}

            {renderPrimaryContent()}
          </div>
        ) : (
          renderPrimaryContent()
        )}
      </div>

      {isQuickJumpNavVisibleOnMobile && (
        <QuickJumpNav
          steps={steps}
          currentViewedStepId={currentViewedStepId}
          onStepSelect={handleStepSelect}
          isMobileBottomNavActive={isMobileBottomNavActive}
          stepCompletionStatus={stepCompletionStatus}
        />
      )}

      <BottomFooterBar />

      {isMobileBottomNavActive && (
        <MobileBottomNav
          onMenuClick={toggleMobileMenu}
          onNextClick={handleNextStep}
          onBackClick={handleBackStep}
          isBackDisabled={isBackDisabled}
        />
      )}

      <div className="h-14 md:hidden" />
      <footer className="hidden md:block text-center pt-3 pb-[calc(0.75rem+env(safe-area-inset-bottom))] text-gray-500 text-base bg-gray-50">
        &copy; {new Date().getFullYear()} Veros Credit. All rights reserved.
      </footer>
    </div>
  );
};

export default App;
